/**
 * Replace File Section Component
 *
 * Allows creators to replace an asset's file while maintaining asset identity.
 * Shows side-by-side comparison of current and new files with playback support.
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  SwapHoriz,
  Cancel,
  CheckCircle,
  Close,
} from '@mui/icons-material';
import { Asset } from '../../types/asset.types';
import AssetThumbnail from './AssetThumbnail';

// File type validation per layer (from backend spec)
const ALLOWED_FILE_TYPES: Record<string, string[]> = {
  G: ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/m4a'], // Songs
  L: ['image/jpeg', 'image/png', 'image/webp'], // Looks
  W: ['video/mp4', 'video/quicktime', 'video/x-msvideo'], // Worlds
  M: ['video/mp4', 'video/quicktime', 'video/x-msvideo'], // Moves
  S: ['image/jpeg', 'image/png', 'image/webp'], // Stars
};

// Max file size: 32MB
const MAX_FILE_SIZE = 32 * 1024 * 1024;

interface ReplaceFileSectionProps {
  asset: Asset;
  onFileReplaced: () => void; // Callback after successful replacement
}

const ReplaceFileSection: React.FC<ReplaceFileSectionProps> = ({
  asset,
  onFileReplaced,
}) => {
  const [isReplacing, setIsReplacing] = useState(false);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newFilePreview, setNewFilePreview] = useState<string | null>(null);
  const [versionNotes, setVersionNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate file type against layer
  const validateFileType = (file: File): boolean => {
    const allowedTypes = ALLOWED_FILE_TYPES[asset.layer] || [];
    return allowedTypes.includes(file.type);
  };

  // Validate file size
  const validateFileSize = (file: File): boolean => {
    return file.size <= MAX_FILE_SIZE;
  };

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    setError(null);

    // Validate file type
    if (!validateFileType(file)) {
      const allowedTypes = ALLOWED_FILE_TYPES[asset.layer] || [];
      setError(
        `Invalid file type. Allowed types for ${asset.layer} layer: ${allowedTypes.join(', ')}`
      );
      return;
    }

    // Validate file size
    if (!validateFileSize(file)) {
      setError(
        `File size exceeds maximum allowed size of ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB`
      );
      return;
    }

    setNewFile(file);

    // Create preview URL for images and videos
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const previewUrl = URL.createObjectURL(file);
      setNewFilePreview(previewUrl);
    }
  }, [asset.layer]);

  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle drag and drop
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle replace file submission
  const handleReplaceFile = async () => {
    if (!newFile) return;

    setIsUploading(true);
    setError(null);

    try {
      // Import assetService dynamically to avoid circular dependencies
      const { default: assetService } = await import('../../api/assetService');

      // Create FormData
      const formData = new FormData();
      formData.append('file', newFile);
      if (versionNotes.trim()) {
        formData.append('versionNotes', versionNotes.trim());
      }

      // Call replace file API
      await assetService.replaceAssetFile(asset.id!, formData);

      // Success - reset state and notify parent
      setIsReplacing(false);
      setNewFile(null);
      setNewFilePreview(null);
      setVersionNotes('');
      onFileReplaced();
    } catch (err: any) {
      console.error('❌ [REPLACE FILE] Error:', err);
      setError(err.message || 'Failed to replace file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setIsReplacing(false);
    setNewFile(null);
    setNewFilePreview(null);
    setVersionNotes('');
    setError(null);
    if (newFilePreview) {
      URL.revokeObjectURL(newFilePreview);
    }
  };

  // Cleanup preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (newFilePreview) {
        URL.revokeObjectURL(newFilePreview);
      }
    };
  }, [newFilePreview]);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHoriz /> Asset File Replacement
        </Typography>
        {!isReplacing && (
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setIsReplacing(true)}
          >
            Replace File
          </Button>
        )}
      </Box>

      {!isReplacing ? (
        // Show current file preview
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current file: {(asset.metadata?.videoUrl ?? asset.metadata?.fileUrl ?? asset.files?.[0]?.url) ? (asset.metadata?.videoUrl ?? asset.metadata?.fileUrl ?? asset.files?.[0]?.url)?.split('/').pop() : 'N/A'}
          </Typography>
          <AssetThumbnail asset={asset} width={280} height={280} />
        </Box>
      ) : (
        // Show replacement interface
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload a new file to replace the current asset. The asset identity (HFN, NNA address) will remain unchanged.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Side-by-side comparison */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3, alignItems: 'flex-start' }}>
            {/* Current File */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Current File
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <AssetThumbnail asset={asset} width={240} height={240} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {(asset.metadata?.videoUrl ?? asset.metadata?.fileUrl ?? asset.files?.[0]?.url) ? (asset.metadata?.videoUrl ?? asset.metadata?.fileUrl ?? asset.files?.[0]?.url)?.split('/').pop() : 'N/A'}
                </Typography>
              </Paper>
            </Box>

            {/* Arrow */}
            <Box sx={{ display: 'flex', alignItems: 'center', pt: 6 }}>
              <SwapHoriz sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>

            {/* New File */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                New File
              </Typography>
              {!newFile ? (
                // File upload area
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: isDragOver ? '2px dashed' : '2px dashed',
                    borderColor: isDragOver ? 'primary.main' : 'grey.300',
                    backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CloudUpload sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    Drag & drop file here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Max size: 32MB
                  </Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept={ALLOWED_FILE_TYPES[asset.layer]?.join(',')}
                    onChange={handleFileInputChange}
                  />
                </Paper>
              ) : (
                // Show selected file preview
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', position: 'relative' }}>
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 4, right: 4 }}
                    onClick={() => {
                      setNewFile(null);
                      setNewFilePreview(null);
                      if (newFilePreview) URL.revokeObjectURL(newFilePreview);
                    }}
                  >
                    <Close />
                  </IconButton>
                  {newFilePreview ? (
                    newFile.type.startsWith('image/') ? (
                      <img
                        src={newFilePreview}
                        alt="New file preview"
                        style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain' }}
                      />
                    ) : (
                      <video
                        src={newFilePreview}
                        style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain' }}
                        controls
                      />
                    )
                  ) : (
                    <Box sx={{ py: 10 }}>
                      <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 1 }} />
                      <Typography variant="body2">Audio file selected</Typography>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {newFile.name} ({(newFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Version Notes */}
          <TextField
            fullWidth
            label="Version Notes"
            placeholder="Describe what changed (optional)"
            multiline
            rows={3}
            value={versionNotes}
            onChange={(e) => setVersionNotes(e.target.value)}
            sx={{ mb: 3 }}
            helperText="These notes will appear in the version history"
          />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isUploading ? <CircularProgress size={20} /> : <CheckCircle />}
              onClick={handleReplaceFile}
              disabled={!newFile || isUploading}
            >
              {isUploading ? 'Replacing File...' : 'Confirm Replacement'}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ReplaceFileSection;
